import React, {
  useRef,
  useLayoutEffect,
  forwardRef,
  useEffect,
  useState,
} from "react"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image"

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"

import tw, { css } from "twin.macro"
import { greenHeading, paragraphStyles } from "@styles/ui"

import Container from "@components/structure/Container"
import ColorLines from "@homepage/ColorLines"
import Circle from "@homepage/Circle"
import Video from "@components/Video"
import ModalVideo from "@components/ModalVideo"

import useMobileDetection from "@utils/useMobileDetection"

const SectionOne = forwardRef((props, ref) => {
  gsap.registerPlugin(ScrollTrigger)

  const hOneRef = useRef(null)
  const sectionTextRef = useRef(null)
  const bgImgRef = useRef(null)
  const pinned = useRef(null)
  const secondTextDiv = useRef(null)
  const copyOuterContainer = useRef(null)
  const greenHeadingRef = useRef(null)
  const heroVideoRef = useRef(null)
  const overlayVideoRef = useRef(null)

  const isMobile = useMobileDetection()

  const [top, setTop] = useState("0px")

  const vimeoVideoLink =
    "https://player.vimeo.com/video/902282490?badge=0&autopause=0&player_id=0&app_id=58479";

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: ref.current,
      pin: ref.current,
      pinSpacing: false,
      start: "top top",
      end: "150px top",
      scrub: 1,
      toggleActions: "restart none none reverse",
    },
  })
  const tl = useRef(timeline)

  const data = useStaticQuery(graphql`
    query {
      heroMobile: file(relativePath: { eq: "homepage/hero-bg-mobile.png" }) {
        childImageSharp {
          gatsbyImageData(width: 750, quality: 80)
        }
      }
      heroDesktop: file(relativePath: { eq: "homepage/hero-bg.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 80)
        }
      }
      heroTablet: file(relativePath: { eq: "homepage/hero-bg-tablet.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 80)
        }
      }
    }
  `)

  const heroImg = withArtDirection(getImage(data.heroDesktop), [
    {
      media: "(max-width: 767px)",
      image: getImage(data.heroMobile),
    },
    {
      media: "(max-width: 1023px)",
      image: getImage(data.heroTablet),
    },
  ])

  const videoImg = getImage(data.heroDesktop)

  const pStyles = [
    paragraphStyles,
    tw`leading-[1.1] mb-3 text-tan md:(text-lg leading-120)`,
  ]

  const overlay = tw`mobileOnly:h-screen bg-purple-gradient w-full h-[150vh] absolute top-0 left-0 opacity-0 lg:(hidden)`

  const sectionHeading = tw`text-white font-kanit tracking-[0.005rem] font-extrabold leading-[0] text-[2.5rem] z-10 !left-0 !relative md:(text-6xl font-bold !left-0 !max-w-none !w-full !transform-none) xl:(text-7xl)`

  const slideUp = tw`mobileOnly:(flex items-center) overflow-hidden relative h-[38px] md:(h-[56px]) xl:(h-[63px])`

  let stickyIsiHeight = 0
  setTimeout(() => {
    if (typeof window !== "undefined") {
      stickyIsiHeight = document.querySelector(
        '[data-id="sticky-isi"]'
      ).offsetHeight
    }
  }, 500)

  useEffect(() => {
    setTop(`${greenHeadingRef.current.offsetTop}px`)
  }, [])

  useLayoutEffect(() => {
    let mm = gsap.matchMedia()

    mm.add(
      {
        isDesktop: `(min-width: 1024px)`,
        isMobile: `(max-width: 1023px)`,
        isMinHeight: `(max-height: 900px)`,
      },
      context => {
        let { isDesktop, isMobile, isMinHeight } = context.conditions

        if (isMobile) {
          gsap.fromTo(
            "#section-one-heading span, .heading-one-container button[data-id='modal-video']",
            { y: 600 },
            {
              ease: "power2.out",
              delay: 0.6,
              duration: 0.45,
              opacity: 1,
              y: 0,
            }
          )
        } else {
          tl.current
            .fromTo(
              copyOuterContainer.current,
              { top: "100vh" },
              {
                ease: "power2.out",
                delay: 1,
                duration: 2,
                top: "0",
              }
            )
            .fromTo(
              bgImgRef.current,
              { zIndex: 1 },
              { zIndex: isMinHeight ? 0 : 1 }
            )
            .fromTo(
              "#section-one-heading span",
              { y: 600 },
              {
                ease: "power2.out",
                delay: 2,
                duration: 10,
                opacity: 1,
                y: 0,
              }
            )
        }

        ScrollTrigger.create({
          trigger: hOneRef.current,
          start: "0% 0%",
          pin: isDesktop ? hOneRef.current : false,
          pinSpacing: isDesktop ? false : true,
          endTrigger: sectionTextRef.current,
          end: isDesktop ? "+=120vh" : "120vh",
          scrub: 1,
          toggleActions: "restart none none reverse",
          animations: tl.current,
        })

        tl.current
          .fromTo(
            "#overlay",
            { opacity: 0 },
            { opacity: 1, ease: "power2.out", duration: 2 }
          )
          .to(sectionTextRef.current, {
            delay: 2,
            y: 0,
            ease: "power2.out",
            duration: 10,
          })

          .fromTo(
            pinned.current,
            { y: 0, top: isDesktop ? "36vh" : "175px" },
            {
              y: 0,
              top: isDesktop ? "28vh" : "120px",
              ease: "power2.out",
              delay: 2.25,
              duration: 5,
            },
            0
          )
          .to("#sectionOne-textContent", {
            height: () => {
              if (isDesktop && isMinHeight) {
                return `${
                  copyOuterContainer.current.offsetHeight - stickyIsiHeight
                }px`
              } else {
                return "auto"
              }
            },
          })
          .to(copyOuterContainer.current, {
            overflowY: isMobile ? "scroll" : "hidden",
          })
          .to(copyOuterContainer.current, {
            overflowY: isMinHeight ? "scroll" : "hidden",
          })
      }
    )
    return () => mm.revert()
  }, [])

  return (
    <section
      id="sectionOne"
      className="hp-sections"
      tw="mobileOnly:(h-[130vh]) relative overflow-hidden h-[150vh] z-[1] !top-0 !translate-y-0 bg-purple-gradient"
      ref={ref}
    >
      <div
        ref={bgImgRef}
        id="sectionOne-bg-img"
        tw="mobileOnly:h-screen w-full h-[100vh] absolute top-0 left-0"
      >
        {isMobile === true ? (
          <GatsbyImage
            alt=""
            tw="!w-full !h-full absolute top-0 left-0 aspect-[16/9]"
            image={heroImg}
            objectFit="cover"
            objectPosition="top"
            className="bgImgRef"
          />
        ) : (
          <Video
            ref={heroVideoRef}
            videoSrc={vimeoVideoLink}
            title="CorMedix DefenCath Homepage Video"
            poster={heroImg}
            autoplay={true}
            controls={false}
            videoId="hero-video"
            muted={true}
            loop={false}
            showMuteBtn={true}
          />
        )}
      </div>
      <div id="overlay" css={overlay}></div>

      <div
        tw="relative w-full h-[inherit] left-0 top-0"
        className="hide-scrollbar"
        ref={copyOuterContainer}
      >
        <div
          ref={pinned}
          id="section-one-container"
          tw="absolute w-full top-[28vh] 3xl:overflow-visible"
        >
          <Container
            css={tw`!relative px-4 pl-6 lg:(pt-0) 3xl:(max-w-[82rem] px-14)`}
          >
            <div
              className="heading-one-container"
              tw="overflow-y-hidden lg:w-[600px]"
            >
              <h1 ref={hOneRef} id="section-one-heading" css={sectionHeading}>
                <div css={[slideUp]}>
                  <span tw="text-green absolute block opacity-0">
                    Protect the
                  </span>
                </div>

                <div css={[slideUp]}>
                  <span tw="text-green absolute block -mt-1 opacity-0">
                    central line
                  </span>
                </div>
              </h1>

              <ModalVideo
                targetModal="modal-video"
                triggerContent={"See within"}
                triggerStyles={[
                  tw`opacity-0 relative z-10 bg-violet text-tan font-kanit text-base leading-5 font-bold tracking-[0.005rem] rounded-[6.25rem] py-2 px-6 mt-6`,
                  isMobile ? tw`visible block` : tw`invisible hidden`,
                ]}
                modalGtmName="trigger-video-modal"
                modalTitle="Video modal"
                modalPanelStyles={tw`bg-black/50`}
                dialogPanelStyles={tw`bg-transparent rounded-none w-full lg:(w-auto)`}
                additionalAction={() => {
                  const heroVideo = document.querySelector(
                    '[data-id="hero-video"] video'
                  )
                  if (heroVideo) {
                    heroVideo.pause()
                    heroVideo.currentTime = 0
                  }
                  setTimeout(() => {
                    const modalVideoBtn = document.querySelector(
                      '[data-id="play-video"]'
                    )
                    if (modalVideoBtn) {
                      modalVideoBtn.click()
                    }
                  }, 200)
                }}
              >
                <div tw="relative w-full h-full">
                  <Video
                    ref={overlayVideoRef}
                    videoSrc={vimeoVideoLink}
                    title="CorMedix DefenCath Homepage Video"
                    poster={videoImg}
                    autoplay={true}
                    videoId="video-in-modal"
                    showMuteBtn={false}
                    muted={true}
                  />
                </div>
              </ModalVideo>
            </div>
          </Container>

          <div ref={sectionTextRef} tw="translate-y-[100vh]">
            <Container
              id="sectionOne-textContent"
              className="mobileOnly:content-baseline"
              tw="overflow-hidden pb-[33rem] grid pl-12 pr-14 lg:(overflow-visible pb-0 pr-0 grid-cols-12 pl-6) xl:pl-0 3xl:(max-w-[82rem] px-14)"
            >
              <div
                css={[
                  tw`absolute left-[20px] h-[500px] lg:(block right-0 left-[0.063rem] m-auto z-10 h-[110vh] -top-[70px]) xl:(h-[120vh])`,
                  css`
                    top: ${top};
                  `,
                ]}
              >
                <ColorLines green tw="mt-1" />
                <Circle tw="bg-green absolute m-auto top-0 -left-[6px] right-0 lg:left-0" />
              </div>

              <div tw="mt-6 lg:(col-start-1 col-span-5) xl:(col-span-6 max-w-[568px])">
                <p css={pStyles}>
                  <b tw="font-extrabold">LIMITED POPULATION:</b> DefenCath
                  <sup>&reg;</sup> is indicated to reduce the incidence of
                  catheter-related bloodstream infections (CRBSI) in adult
                  patients with kidney failure receiving chronic hemodialysis
                  (HD) through a central venous catheter (CVC). This drug is
                  indicated for use in a limited and specific population of
                  patients.
                </p>
                <p css={pStyles}>
                  <b tw="font-extrabold underline decoration-[1.5px] md:(decoration-2)">
                    Limitations of Use
                  </b>
                  <br /> The safety and effectiveness of DefenCath have not been
                  established for use in populations other than adult patients
                  with kidney failure receiving chronic HD through a CVC.
                </p>
              </div>

              <div
                ref={secondTextDiv}
                tw="mt-8 lg:(max-w-[21.3rem] -mt-[4.6rem] z-10 col-start-8 col-span-9) xl:(max-w-[24.125rem] ml-5) 2xl:(max-w-[32.125rem])"
              >
                <h2
                  css={[greenHeading, tw`mb-2.5 lg:(leading-[0.9])`]}
                  ref={greenHeadingRef}
                >
                  Introducing DefenCath
                </h2>

                <p css={pStyles}>
                  The first and only FDA-approved catheter lock solution (CLS)
                  proven to significantly reduce the incidence of
                  catheter-related bloodstream infections (CRBSIs) in adult
                  patients receiving chronic hemodialysis (HD) with central
                  venous catheters (CVC).<sup tw="font-normal">1</sup>
                </p>

                <p css={pStyles}>
                  DefenCath reduces the incidence of CRBSIs before infections
                  can start, so you can protect the entire length of the lumen.
                  <sup tw="font-normal -top-[0.3em]">1</sup>
                </p>
              </div>
            </Container>
          </div>
        </div>
      </div>
    </section>
  )
})

export default SectionOne
